import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react'

import { useLazyRef } from '../useLazyRef'

/**
 * Equivalent to `useState`, except the value is allowed to be modified before
 * being returned each render.
 *
 * @example
 * ```ts
 * const [renderCount, setRenderCount] = useDerivedState(() => 0, (current) => current + 1))
 *
 * const resetCount = () => setRenderCount(0)
 * ```
 */
export const useDerivedState = <T>(
	getInitial: () => T,
	getDerived: (previous: T) => T,
): [state: T, setState: Dispatch<SetStateAction<T>>] => {
	const ref = useLazyRef(getInitial)
	const [, setDummyState] = useState({})
	const setState = useCallback(
		(action: SetStateAction<T>) => {
			if (typeof action === 'function') {
				setDummyState(() => {
					ref.current = (action as any)(ref.current)
					return {}
				})
			} else {
				ref.current = action
				setDummyState({})
			}
		},
		[ref],
	)

	const derived = getDerived(ref.current)

	useEffect(() => {
		ref.current = derived
	})

	return [derived, setState]
}
